/**
 * @module game-config
 * Single source of truth for all tunable values.
 *
 * RULE: No magic numbers anywhere in game logic. Always reference this file.
 * Changing a value here changes it everywhere — no grepping required.
 */

// ---------------------------------------------------------------------------
// Shared types (mirrors src/types.ts to avoid circular imports from config)
// ---------------------------------------------------------------------------
export type FruitType =
  | 'apel'      // Apple
  | 'semangka'  // Watermelon
  | 'jeruk'     // Orange
  | 'pisang'    // Banana
  | 'stroberi'  // Strawberry
  | 'nanas'     // Pineapple
  | 'kiwi'
  | 'naga';     // Dragon fruit

export type PowerUpType = 'freeze' | 'double' | 'magnet';
export type ObstacleType = 'ice' | 'rotten';
export type GameMode = 'classic' | 'zen';

// ---------------------------------------------------------------------------
// Per-fruit configuration
// ---------------------------------------------------------------------------
export interface FruitConfig {
  /** Hex colour used for juice particles on slash. */
  juiceColor: string;
  /** Hitbox radius in canvas pixels (at 640×480 reference resolution). */
  hitRadius: number;
  /** Base score value for slashing this fruit (before multipliers). */
  basePoints: number;
  /** Primary SVG fill colour (for reference / tinting). */
  primaryColor: string;
}

// ---------------------------------------------------------------------------
// Master config schema
// ---------------------------------------------------------------------------
export interface GameConfig {
  slash: {
    /** Minimum pointer velocity (px/s) required to register a slash gesture. */
    minVelocity: number;
    /**
     * Consecutive fast samples required before slash segments fire.
     * Jitter/re-lock spikes last one sample; real slashes sustain 2+.
     */
    minStreakSamples: number;
    /**
     * Slash hitbox multiplier for fruits and power-ups. Sprites render at
     * ~1.7× hitRadius, so the collision radius must at least match the VISUAL
     * size — otherwise slashes that visibly cross a fruit still miss.
     * Bombs intentionally use the raw (small) hitRadius — accidental bomb
     * hits should be rare.
     */
    fruitHitboxScale: number;
    /** Number of historical positions kept in the blade trail queue (per hand). */
    trailLength: number;
    /** One-euro filter parameters for landmark smoothing. */
    smoothing: {
      /** Minimum cutoff frequency (Hz) — controls low-speed smoothing. */
      minCutoff: number;
      /** Beta — controls high-speed responsiveness. Higher = less lag at speed. */
      beta: number;
      /** Derivative cutoff frequency (Hz) — usually left at 1.0. */
      dCutoff: number;
    };
    /** Blade trail colors indexed by combo count bucket. */
    trailColors: {
      combo0: string;    // 0 combo — default
      combo1: string;    // 1–2 combo
      combo3: string;    // 3–4 combo
      combo5plus: string; // 5+ combo (with glow)
    };
    /** Trail line width at newest point (tapers to 1px at oldest). */
    trailWidthMax: number;
    /** Duration (ms) for trail to fully fade after hand stops. */
    trailFadeMs: number;
  };

  dwell: {
    /** Duration (ms) a pointer must stay inside a button to activate it. */
    holdDuration: number;
    /** Radius (px) of the circular progress ring drawn over dwell targets. */
    ringRadius: number;
    /** Ring stroke width in px. */
    ringStrokeWidth: number;
    /** Color of the dwell ring. */
    ringColor: string;
  };

  fruit: {
    /** Base interval (ms) between spawns. Decreases as difficulty scales. */
    spawnInterval: number;
    /** Launch speed (px/s). Increases as difficulty scales. */
    baseSpeed: number;
    /** Gravity acceleration (px/s²) applied each physics frame. */
    gravity: number;
    /** Probability [0–1] that a spawned fruit is the critical (golden) variant. */
    criticalChance: number;
    /** Default base score per fruit (overridden per type in fruits map). */
    basePoints: number;
    /** Score multiplier applied to critical fruit hits (stacks with combo). */
    criticalMultiplier: number;
    /** Points deducted when a rotten fruit is slashed. Score floor: 0. */
    rottenPenalty: number;
    /** Default hitbox radius in px (overridden per type in fruits map). */
    hitRadius: number;
    /**
     * Spawn horizontal range as fraction of canvas width.
     * Fruits spawn at x ∈ [spawnXMin, spawnXMax] * canvasWidth.
     */
    spawnXMin: number;
    spawnXMax: number;
    /** Launch angle range (degrees from horizontal). */
    launchAngleMin: number;
    launchAngleMax: number;
    /**
     * Fruit arc peak as a fraction of canvas height — vy is derived from this
     * (v = √(2·g·rise)) so fruits are reachable on any viewport size.
     */
    peakHeightMin: number;
    peakHeightMax: number;
    /** Max spin rate (rad/s) for airborne fruit — per-fruit rate/direction derives from its id. */
    spinSpeedMax: number;
  };

  /** Per-type overrides for juice colour, hitbox radius, and base points. */
  fruits: Record<FruitType, FruitConfig>;

  powerup: {
    /** Probability [0–1] that a given spawn slot produces a power-up instead of fruit. */
    spawnChance: number;
    /** Duration (ms) of the Freeze effect (all entities at 20% speed). */
    freezeDuration: number;
    /** Duration (ms) of the Double Points effect (all score × 2). */
    doublePointsDuration: number;
    /** Duration (ms) of the Magnet effect. */
    magnetDuration: number;
    /** Radius (px) within which the magnet attracts fruit toward the pointer. */
    magnetRadius: number;
    /** Speed (px/s) at which fruit are pulled toward the pointer under magnet. */
    magnetPullSpeed: number;
    /** Velocity scale applied to entities when Freeze is active (0.2 = 20% speed). */
    freezeVelocityScale: number;
    /** Hitbox radius for all power-up entities in px. */
    hitRadius: number;
  };

  bomb: {
    /** Probability [0–1] that a given spawn slot produces a bomb (Classic mode only). */
    spawnChance: number;
    /** Hitbox radius in px — slightly larger than fruit to be harder to avoid. */
    hitRadius: number;
  };

  obstacle: {
    /** Probability [0–1] for ice obstacle per spawn slot (both modes). */
    iceSpawnChance: number;
    /** Probability [0–1] for rotten fruit per spawn slot (both modes). */
    rottenSpawnChance: number;
    /** Duration (ms) the slash penalty lasts after hitting an ice obstacle. */
    icePenaltyDuration: number;
    /** Multiplier applied to minVelocity during the ice penalty period. */
    icePenaltyVelocityScale: number;
    /** Hitbox radius for obstacle entities in px. */
    hitRadius: number;
  };

  feel: {
    /** Duration (ms) of hitstop (timeScale → 0) on any slash hit. */
    hitstopMs: number;
    /**
     * Maximum displacement magnitude (px) for normal slash screen shake.
     * Bomb explosion uses 3× this value.
     */
    screenShakeIntensity: number;
    /** Duration (ms) of a normal slash screen shake. */
    screenShakeDuration: number;
    /** Time-scale factor applied during slow-motion events. */
    slowMoScale: number;
    /** Duration (ms) of slow-motion phase on critical fruit hit. */
    slowMoDurationMs: number;
    /** Duration (ms) of the ease-back from slowMoScale → 1.0 after slow-mo. */
    slowMoEaseMs: number;
    /** Time-scale factor during frenzy wave slow-mo intro. */
    frenzySlowMoScale: number;
    /** Duration (ms) of frenzy slow-mo intro. */
    frenzySlowMoDuration: number;
  };

  combo: {
    /** Maximum combo multiplier cap. */
    maxMultiplier: number;
    /** Howler playback rate increment per combo level (e.g., 0.15 → +15% speed per hit). */
    pitchIncrement: number;
    /** Maximum Howler rate for combo audio (capped). */
    maxPitch: number;
    /** Duration (ms) for combo popup text to display before fading. */
    popupFadeMs: number;
    /**
     * Combo count thresholds to multiplier map.
     * Key: minimum combo count. Value: multiplier applied.
     * e.g., { 1: 1, 2: 2, 3: 3, 4: 4 }
     */
    multiplierThresholds: Record<number, number>;
  };

  game: {
    /** Lives granted in Classic mode. */
    lives: number;
    /** Zen mode duration in seconds. */
    zenModeDuration: number;
    /** Interval (ms) between automatic frenzy waves in Classic mode. */
    frenzyInterval: number;
    /** Interval (ms) between automatic frenzy waves in Zen mode. */
    frenzyIntervalZen: number;
    /** [min, max] fruit count spawned in a single frenzy wave. */
    frenzyFruitCount: [number, number];
    /** Fruits allowed in play simultaneously at game start. */
    concurrentStart: number;
    /** Interval (ms) after which the concurrent-fruit cap grows by one. */
    concurrentRampMs: number;
    /** Interval (ms) between difficulty escalation steps. */
    difficultyTickMs: number;
    /** Amount (ms) to reduce spawnInterval each difficulty tick. */
    spawnIntervalDelta: number;
    /** Amount (px/s) to increase baseSpeed each difficulty tick. */
    speedDelta: number;
    /** Minimum spawn interval floor (ms) — difficulty cannot push below this. */
    minSpawnInterval: number;
    /** Maximum fruit speed cap (px/s) — difficulty cannot push above this. */
    maxSpeed: number;
  };

  /** HUD & UI visual configuration. */
  ui: {
    /** Cursor circle radius in px. */
    cursorRadius: number;
    /** Cursor color (CSS color string). */
    cursorColor: string;
    /** Opacity of cursor glow. */
    cursorGlowAlpha: number;
    /** Duration (ms) for cursor to fade out after hand loss. */
    cursorFadeOutMs: number;
    /** Duration (ms) for cursor to fade back in on hand detect. */
    cursorFadeInMs: number;
    /** Duration (ms) of the "FRENZY!" banner display. */
    frenzyBannerMs: number;
    /** Camera feed opacity on desktop (mobile uses perf.mobile.videoOpacity). */
    videoOpacity: number;
    /** Soft drop shadow under entity sprites — blur radius (px). */
    spriteShadowBlur: number;
    /** Sprite shadow X offset (px). */
    spriteShadowOffsetX: number;
    /** Sprite shadow Y offset (px). */
    spriteShadowOffsetY: number;
    /** Sprite shadow opacity [0–1]. */
    spriteShadowAlpha: number;
  };

  /** Particle system configuration. */
  particles: {
    /** Object pool maximum size. */
    poolSize: number;
    /** [min, max] particles per normal fruit slash. */
    countRange: [number, number];
    /** Multiplier applied to countRange for critical fruit. */
    criticalCountMultiplier: number;
    /** [min, max] initial particle speed (px/s). */
    speedRange: [number, number];
    /** Deceleration rate (px/s²). */
    deceleration: number;
    /** Particle lifetime (ms). */
    lifetimeMs: number;
    /** [min, max] particle radius (px). */
    radiusRange: [number, number];
    /** Spread angle (degrees) from slash direction (particles fan out ± this). */
    spreadAngle: number;
  };

  /**
   * Camera & tracking configuration.
   * NOTE: slash velocity threshold lives at CONFIG.slash.minVelocity — do not
   * duplicate it here. Tracking code reads CONFIG.slash.minVelocity directly.
   */
  tracking: {
    /** Fingertip landmark index used for pointer and slash detection. */
    fingertipLandmarkIndex: number;
    /** Expected tracking interval (ms) used for interpolation alpha calculation. */
    expectedIntervalMs: number;
    /** Duration (ms) of no-hand state before pointer is considered lost. */
    handLostDebounceMs: number;
    /**
     * During PLAYING with hand input active: auto-pause after this long with
     * NO fresh hand pointer AND no mouse/touch activity. Losing lives to a
     * tracking dropout (hand left frame, lighting change) is unfair.
     */
    autoPauseAfterMs: number;
    /**
     * Camera dropout grace (ms). Fast hands motion-blur the camera frame and
     * MediaPipe loses detection for a few frames — the cursor/trail COAST
     * through the gap (decaying velocity) instead of blinking out.
     */
    coastMs: number;
    /** Velocity decay time-constant (ms) for dropout coasting. */
    coastTauMs: number;
    /**
     * The mouse counts as an input source only this long after its last
     * movement. An idle mouse parked on screen used to hijack the blade the
     * moment hand tracking blipped — the cursor "jumped" to a stale point.
     * Idle mice are hidden AND ignored; they reappear instantly on movement.
     */
    mouseIdleHideMs: number;
    /**
     * Requested camera frame rate. 60fps halves per-frame motion blur and
     * doubles sample density where the hardware supports it (graceful
     * fallback to 30 elsewhere — `ideal`, not `exact`).
     */
    cameraFrameRateIdeal: number;
    /**
     * MediaPipe confidence thresholds. LOWER presence/tracking values keep
     * the tracker locked onto fast (motion-blurred) hands instead of
     * dropping out. Detection (the ACQUISITION gate) is also lowered: palm
     * detection runs on a ~192px downscaled frame, so a hand at ~1.5m spans
     * only ~16px there — the 0.5 default refuses to acquire it at all, and
     * the lowered presence/tracking locks can never rescue a hand that was
     * never acquired. Far-hand play requires a permissive acquisition gate.
     */
    confidence: {
      detection: number;
      presence: number;
      tracking: number;
    };
    /**
     * Tracker resilience knobs — shared by the worker and main-thread paths.
     */
    failover: {
      /**
       * Max wait (ms) for the worker's WASM + model init before giving up
       * and falling back to main-thread inference (slow networks).
       */
      workerInitTimeoutMs: number;
      /**
       * Consecutive detectForVideo failures before a tracker disables
       * itself (worker: posts fatal → main-thread fallback; main thread:
       * destroys the landmarker → pointer fallback).
       */
      maxConsecutiveFailures: number;
    };

    /**
     * Hand-identity gating — fixes cursor jumps caused by unstable MediaPipe
     * hand ordering and transient false-positive detections admitted by the
     * lowered detection confidence (0.35).
     *
     * MediaPipe does not guarantee that hand[0] in frame N is the same physical
     * hand as hand[0] in frame N+1; at confidence 0.35, spurious detections
     * (faces, shoulders, reflections) can appear for a single frame and snap the
     * cursor to an unrelated screen region. This group governs:
     *  – nearest-neighbour slot continuity (index-swap-safe matching),
     *  – adaptive match radius so fast slashes at low tracking fps still match,
     *  – debounced acquisition that silently drops single-frame phantoms.
     */
    identity: {
      /**
       * Real hands move at most ~4 normalized-units/s; the per-frame match
       * radius scales with dt so fast slashes at low tracking fps still match.
       */
      maxMatchSpeedNormPerSec: number;
      /** Floor (normalized units) for the slot-match radius at tiny dt. */
      maxMatchBaseNorm: number;
      /**
       * Consecutive frames a NEW (or teleported) candidate must persist near
       * the same spot before it is accepted as a pointer — single-frame phantom
       * detections (faces, shoulders at detection confidence 0.35) never become
       * a cursor.
       */
      acquireConfirmFrames: number;
      /** Radius (normalized) for matching a candidate against a pending acquisition across consecutive frames. */
      pendingMatchDistNorm: number;
      /**
       * Slot continuity window (ms) — a slot unseen longer than this is stale
       * and reacquisition requires confirmation + filter reset.
       * Aligned with tracking.coastMs.
       */
      slotStaleMs: number;
    };
  };

  /** READY gate — pre-game camera/hand confirmation + countdown. */
  ready: {
    /** Continuous fresh-hand time (ms) required to confirm tracking works. */
    handConfirmMs: number;
    /** Max wait (ms) for a hand before falling through to the countdown. */
    handWaitTimeoutMs: number;
    /** Max wait (ms) for camera+tracker init before giving up on hand mode. */
    cameraWaitTimeoutMs: number;
    /** Countdown start value (3 → 2 → 1 → play). */
    countdownFrom: number;
    /** Duration (ms) of each countdown step. */
    countdownStepMs: number;
  };

  /** Performance scaling — adaptive tracking + mobile device profile. */
  perf: {
    /**
     * Adaptive detect throttle: detectForVideo is a SYNCHRONOUS WASM call.
     * The tracking interval self-tunes to (measured cost × budgetFactor),
     * clamped to [tracking.expectedIntervalMs, maxIntervalMs] — slow devices
     * automatically drop tracking rate instead of starving the render loop.
     */
    adaptiveTracking: {
      /** EMA smoothing factor for the measured detect cost. */
      emaAlpha: number;
      /** Detect interval = EMA cost × this factor. */
      budgetFactor: number;
      /** Hard ceiling (ms) — tracking never drops below ~10fps. */
      maxIntervalMs: number;
    };
    // NOTE: sprite shadows are ALWAYS pre-rendered radial-gradient blobs
    // (drawImage'd under each sprite) — the per-draw ctx.shadowBlur path was
    // removed entirely because it forces a blur rasterization pass per sprite
    // per frame, a major fill-rate killer on mobile AND lower-end desktop
    // GPUs. There is intentionally no toggle: shadowBlur must not come back.
    /** Overrides applied when the device's primary pointer is coarse (mobile). */
    mobile: {
      /** Track one hand only — halves inference cost on phone GPUs. */
      numHands: number;
      /** Multiplier on juice-particle counts. */
      particleScale: number;
      /** Camera feed opacity (replaces the CSS brightness/saturate filter). */
      videoOpacity: number;
    };
  };

  /** Colour map for juice particles by fruit type (convenience alias). */
  juiceColors: Record<FruitType, string>;
}

// ---------------------------------------------------------------------------
// The CONFIG object — all values per blueprint + PRD spec
// ---------------------------------------------------------------------------
export const CONFIG: GameConfig = {
  slash: {
    minVelocity: 450, // raised from 300 — resting-hand jitter was crossing the old threshold
    minStreakSamples: 2,
    fruitHitboxScale: 1.9, // slightly more generous than the 1.7× sprite visual
    trailLength: 22,
    smoothing: {
      // Unit analysis: the filter runs on NORMALIZED [0..1] coordinates with
      // timestamps in SECONDS. A fast slash moves ~2–3 normalized-units/s.
      //
      // One-euro cutoff formula: fc = minCutoff + beta * |dx/dt|
      // At slash speed (|dx/dt| ≈ 2.5 norm/s):
      //   OLD: fc = 1.2 + 0.05 * 2.5 = 1.325 Hz  → ~120 ms lag during slashes
      //   NEW: fc = 1.4 + 1.5  * 2.5 = 5.15  Hz  → ~31 ms lag during slashes
      //
      // minCutoff 1.4 keeps resting-hand jitter suppressed (~1.4 Hz floor
      // smoothing ≈ 7px p-p jitter on normalised coords → <1px on 640px canvas).
      // beta 1.5 means the cutoff frequency ACTUALLY opens at fast-slash speed —
      // old beta 0.05 left the filter nearly closed (1.3 Hz) even mid-slash.
      minCutoff: 1.4,
      beta: 1.5,
      dCutoff: 1.0,
    },
    trailColors: {
      combo0: '#E0FFFF',     // Light cyan — no combo
      combo1: '#FFE800',     // Yellow — 1–2 combo
      combo3: '#FF8C00',     // Orange — 3–4 combo
      combo5plus: '#FF00CC', // Hot pink + glow — 5+ combo
    },
    trailWidthMax: 14,
    trailFadeMs: 220,
  },

  dwell: {
    holdDuration: 1200,
    ringRadius: 40,
    ringStrokeWidth: 3,
    ringColor: '#FFFFFF',
  },

  fruit: {
    spawnInterval: 1200,
    baseSpeed: 800,
    gravity: 1200,
    criticalChance: 0.05,
    basePoints: 10,
    criticalMultiplier: 5,
    rottenPenalty: 50,
    hitRadius: 30,
    spawnXMin: 0.2,
    spawnXMax: 0.8,
    launchAngleMin: 45,
    launchAngleMax: 135,
    peakHeightMin: 0.6,
    peakHeightMax: 0.85,
    spinSpeedMax: 2.2,
  },

  // NOTE: hitRadius is intentionally UNIFORM across all fruit types — equal
  // size on screen, equal difficulty to hit. Differentiate fruits via points
  // and colors, not hitbox size.
  fruits: {
    apel: {
      juiceColor: '#7BC950',
      hitRadius: 30,
      basePoints: 10,
      primaryColor: '#E8331A',
    },
    semangka: {
      juiceColor: '#FF4D6D',
      hitRadius: 30,
      basePoints: 15,
      primaryColor: '#4CAF50',
    },
    jeruk: {
      juiceColor: '#FF9900',
      hitRadius: 30,
      basePoints: 10,
      primaryColor: '#FF9900',
    },
    pisang: {
      juiceColor: '#FFE066',
      hitRadius: 30,
      basePoints: 10,
      primaryColor: '#FFE066',
    },
    stroberi: {
      juiceColor: '#FF3366',
      hitRadius: 30,
      basePoints: 12,
      primaryColor: '#FF3366',
    },
    nanas: {
      juiceColor: '#FFD700',
      hitRadius: 30,
      basePoints: 12,
      primaryColor: '#FFD700',
    },
    kiwi: {
      juiceColor: '#5D8A2E',
      hitRadius: 30,
      basePoints: 10,
      primaryColor: '#5D8A2E',
    },
    naga: {
      juiceColor: '#FF6EB4',
      hitRadius: 30,
      basePoints: 14,
      primaryColor: '#FF1493',
    },
  },

  powerup: {
    spawnChance: 0.08,
    freezeDuration: 4000,
    doublePointsDuration: 6000,
    magnetDuration: 2000,
    magnetRadius: 300,
    magnetPullSpeed: 200,
    freezeVelocityScale: 0.2,
    hitRadius: 30,
  },

  bomb: {
    spawnChance: 0.07,
    hitRadius: 35,
  },

  obstacle: {
    iceSpawnChance: 0.05,
    rottenSpawnChance: 0.06,
    icePenaltyDuration: 3000,
    icePenaltyVelocityScale: 1.5, // minVelocity × 1.5 during penalty
    hitRadius: 28,
  },

  feel: {
    hitstopMs: 50,
    screenShakeIntensity: 6,
    screenShakeDuration: 200,
    slowMoScale: 0.4,
    slowMoDurationMs: 300,
    slowMoEaseMs: 200,
    frenzySlowMoScale: 0.6,
    frenzySlowMoDuration: 500,
  },

  combo: {
    maxMultiplier: 4,
    pitchIncrement: 0.15,
    maxPitch: 2.0,
    popupFadeMs: 600,
    multiplierThresholds: {
      1: 1,
      2: 2,
      3: 3,
      4: 4,
    },
  },

  game: {
    lives: 3,
    zenModeDuration: 60,
    frenzyInterval: 30000,
    frenzyIntervalZen: 15000,
    frenzyFruitCount: [8, 12],
    concurrentStart: 1,
    concurrentRampMs: 45000,
    difficultyTickMs: 30000,
    spawnIntervalDelta: 100,
    speedDelta: 80,
    minSpawnInterval: 400,
    maxSpeed: 1600,
  },

  ui: {
    cursorRadius: 12,
    cursorColor: '#FFFFFF',
    cursorGlowAlpha: 0.6,
    cursorFadeOutMs: 300,
    cursorFadeInMs: 150,
    frenzyBannerMs: 1500,
    videoOpacity: 0.55,
    spriteShadowBlur: 14,
    spriteShadowOffsetX: 6,
    spriteShadowOffsetY: 12,
    spriteShadowAlpha: 0.4,
  },

  particles: {
    poolSize: 200,
    countRange: [12, 20],
    criticalCountMultiplier: 2,
    speedRange: [200, 500],
    deceleration: 800,
    lifetimeMs: 600,
    radiusRange: [3, 6],
    spreadAngle: 45,
  },

  tracking: {
    fingertipLandmarkIndex: 8,
    expectedIntervalMs: 33, // ~30fps tracking target (floor for the adaptive throttle)
    handLostDebounceMs: 300,
    autoPauseAfterMs: 2500,
    coastMs: 450,
    coastTauMs: 120,
    mouseIdleHideMs: 1500,
    cameraFrameRateIdeal: 60,
    confidence: {
      detection: 0.35, // default 0.5 never acquired far hands (~16px in the 192px palm-detector grid at 1.5m)
      presence: 0.4, // default 0.5 dropped fast-moving hands
      tracking: 0.3, // default 0.5 lost lock on motion blur
    },
    failover: {
      workerInitTimeoutMs: 20000,
      maxConsecutiveFailures: 30,
    },
    identity: {
      maxMatchSpeedNormPerSec: 4,
      maxMatchBaseNorm: 0.05,
      acquireConfirmFrames: 2,
      pendingMatchDistNorm: 0.12,
      slotStaleMs: 450,
    },
    // Slash velocity threshold: read CONFIG.slash.minVelocity — not duplicated here.
  },

  ready: {
    handConfirmMs: 700,
    handWaitTimeoutMs: 20000,
    cameraWaitTimeoutMs: 30000,
    countdownFrom: 3,
    countdownStepMs: 700,
  },

  perf: {
    adaptiveTracking: {
      emaAlpha: 0.15,
      budgetFactor: 2.5,
      maxIntervalMs: 100, // never below ~10fps tracking
    },
    mobile: {
      numHands: 1,
      particleScale: 0.6,
      videoOpacity: 0.45,
    },
  },

  // Convenience alias — identical to fruits[type].juiceColor
  juiceColors: {
    apel: '#7BC950',
    semangka: '#FF4D6D',
    jeruk: '#FF9900',
    pisang: '#FFE066',
    stroberi: '#FF3366',
    nanas: '#FFD700',
    kiwi: '#5D8A2E',
    naga: '#FF6EB4',
  },
} as const;
